<script lang="ts">
  // One's profile: picture and display name. The username and the e-mail are set by an
  // administrator.
  import { onMount } from 'svelte';
  import { api, ApiError } from '../lib/api';
  import { disablePush, enablePush, pushState, type PushState } from '../lib/push';
  import { router } from '../lib/router.svelte';
  import { app } from '../lib/stores.svelte';
  import Avatar from './Avatar.svelte';

  // svelte-ignore state_referenced_locally
  let name = $state(app.me?.display_name ?? '');
  let nameError = $state('');
  let nameDone = $state(false);
  let photoError = $state('');
  let busy = $state(false);
  let picker: HTMLInputElement | undefined = $state();

  const message = (e: unknown) => (e instanceof ApiError ? e.message : 'Une erreur est survenue');
  const MAX_BYTES = 10 * 1024 * 1024;

  function applied(me: Awaited<ReturnType<typeof api.updateProfile>>) {
    app.applyUser(me);
  }

  async function saveName(event: SubmitEvent) {
    event.preventDefault();
    nameError = '';
    nameDone = false;
    try {
      applied(await api.updateProfile({ display_name: name }));
      name = app.me?.display_name ?? name;
      nameDone = true;
    } catch (e) {
      nameError = message(e);
    }
  }

  async function choose(file: File | undefined) {
    if (!file) return;
    photoError = '';
    if (file.size > MAX_BYTES) {
      photoError = "L'image dépasse 10 Mo";
      return;
    }
    busy = true;
    try {
      applied(await api.setAvatar(file));
    } catch (e) {
      photoError = message(e);
    } finally {
      busy = false;
    }
  }

  // Notifications on this browser.
  let push = $state<PushState | null>(null);
  let pushBusy = $state(false);
  let pushError = $state('');
  const publicKey = $derived(app.info?.vapid_public_key ?? null);

  onMount(() => {
    pushState(publicKey)
      .then((s) => (push = s))
      .catch(() => (push = 'unsupported'));
  });

  async function togglePush() {
    pushError = '';
    pushBusy = true;
    try {
      push = push === 'enabled' ? await disablePush() : await enablePush(publicKey ?? '');
    } catch (e) {
      pushError = e instanceof ApiError ? e.message : "Le navigateur n'a pas pu activer les notifications";
    } finally {
      pushBusy = false;
    }
  }

  async function setHideContent(hide: boolean) {
    pushError = '';
    try {
      applied(await api.updateProfile({ push_hide_content: hide }));
    } catch (e) {
      pushError = message(e);
    }
  }

  async function remove() {
    photoError = '';
    busy = true;
    try {
      applied(await api.removeAvatar());
    } catch (e) {
      photoError = message(e);
    } finally {
      busy = false;
    }
  }
</script>

<section class="page" aria-labelledby="profile-title">
  <header>
    <button type="button" class="back" aria-label="Retour aux conversations" onclick={() => router.go('/')}>
      <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
    </button>
    <div>
      <h1 id="profile-title">Profil</h1>
      <p class="sub">Votre nom et votre photo, vus par tous</p>
    </div>
  </header>

  {#if app.me}
    <div class="column">
      <section class="card photo-card" aria-labelledby="photo-title">
        <Avatar user={app.me} size={96} />
        <div class="photo-text">
          <h2 id="photo-title">Photo</h2>
          <p class="hint">JPEG, PNG, GIF ou WebP, 10 Mo au plus. Elle est recadrée au centre, en carré.</p>
          <div class="row">
            <button type="button" class="btn primary" disabled={busy} onclick={() => picker?.click()}>
              {app.me.avatar_url ? 'Changer la photo' : 'Ajouter une photo'}
            </button>
            {#if app.me.avatar_url}
              <button type="button" class="btn" disabled={busy} onclick={remove}>Retirer</button>
            {/if}
          </div>
          <input
            bind:this={picker}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            hidden
            onchange={(e) => {
              const input = e.currentTarget as HTMLInputElement;
              void choose(input.files?.[0]);
              input.value = '';
            }}
          />
          {#if busy}<p class="hint" role="status">Envoi…</p>{/if}
          {#if photoError}<p class="error" role="alert">{photoError}</p>{/if}
        </div>
      </section>

      <section class="card" aria-labelledby="name-title">
        <h2 id="name-title">Identité</h2>
        <form onsubmit={saveName}>
          <label class="field">Nom affiché
            <input class="input" required maxlength="64" bind:value={name} oninput={() => (nameDone = false)} />
          </label>
          {#if nameError}<p class="error" role="alert">{nameError}</p>{/if}
          {#if nameDone}<p class="ok" role="status">Nom enregistré.</p>{/if}
          <button type="submit" class="btn primary" disabled={!name.trim() || name.trim() === app.me.display_name}>Enregistrer</button>
        </form>
        <dl>
          <dt>Nom d'utilisateur</dt>
          <dd>@{app.me.username} <span class="hint">(pour les mentions)</span></dd>
          <dt>E-mail</dt>
          <dd>{app.me.email ?? '—'}</dd>
        </dl>
        <p class="hint">Le nom d'utilisateur et l'e-mail se changent auprès d'un administrateur.</p>
      </section>

      <section class="card" aria-labelledby="push-title">
        <h2 id="push-title">Notifications</h2>
        {#if push === null}
          <p class="hint">…</p>
        {:else if push === 'unsupported'}
          <p class="hint">Ce navigateur ne peut pas recevoir de notifications.</p>
        {:else if push === 'unavailable'}
          <p class="hint">Les notifications du navigateur ne sont pas encore activées sur le serveur.</p>
        {:else if push === 'denied'}
          <p class="hint">
            Les notifications sont bloquées pour ce site. Autorisez-les dans les réglages du navigateur
            (icône à gauche de l'adresse), puis rechargez la page.
          </p>
        {:else}
          <p class="hint">
            {push === 'enabled'
              ? "Ce navigateur reçoit vos notifications quand la messagerie n'est pas au premier plan."
              : 'Recevez une notification pour les messages directs, les mentions et les fils que vous suivez, même fenêtre fermée.'}
          </p>
          <div class="row">
            <button type="button" class={push === 'enabled' ? 'btn' : 'btn primary'} disabled={pushBusy} onclick={togglePush}>
              {push === 'enabled' ? 'Désactiver sur ce navigateur' : 'Activer sur ce navigateur'}
            </button>
          </div>
        {/if}
        <label class="check">
          <input
            type="checkbox"
            checked={app.me.push_hide_content}
            onchange={(e) => setHideContent((e.currentTarget as HTMLInputElement).checked)}
          />
          Masquer le contenu des messages (la notification dit seulement « Nouveau message »)
        </label>
        {#if pushError}<p class="error" role="alert">{pushError}</p>{/if}
      </section>

      <a
        class="more"
        href="/settings/security"
        onclick={(e) => {
          e.preventDefault();
          router.go('/settings/security');
        }}>Sécurité du compte : sessions, mot de passe, codes de secours →</a
      >
    </div>
  {/if}
</section>

<style>
  .page {
    height: 100%;
    overflow-y: auto;
    background: var(--bg-page);
  }
  header {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: var(--header-h);
    padding: 8px 24px;
    border-bottom: 1px solid var(--border);
    background: var(--bg);
  }
  h1 {
    margin: 0;
    font-size: 18px;
    line-height: 26px;
  }
  .sub {
    margin: 0;
    font-size: 13px;
    color: var(--muted);
  }
  .back {
    display: none;
    width: 44px;
    height: 44px;
    margin-left: -12px;
    border: 0;
    background: transparent;
    color: var(--accent-text);
    align-items: center;
    justify-content: center;
  }
  .column {
    max-width: 720px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .card {
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius-l);
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .photo-card {
    flex-direction: row;
    align-items: center;
    gap: 20px;
  }
  .photo-text {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  h2 {
    margin: 0;
    font-size: 16px;
  }
  .hint {
    margin: 0;
    font-size: 13px;
    color: var(--muted);
  }
  .row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
  form .field {
    width: 100%;
    max-width: 380px;
  }
  dl {
    margin: 6px 0 0;
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 4px 16px;
    font-size: 14px;
  }
  dt {
    color: var(--muted);
  }
  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
  .ok {
    margin: 0;
    color: var(--status-done);
    font-weight: 500;
  }
  .check {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    font-size: 14px;
  }
  .check input {
    margin-top: 3px;
    accent-color: var(--accent);
  }
  .more {
    font-weight: 600;
    text-decoration: none;
  }
  @media (max-width: 759px) {
    header {
      padding: 8px 12px;
    }
    .back {
      display: flex;
    }
    .column {
      padding: 12px;
    }
    .photo-card {
      flex-direction: column;
      align-items: flex-start;
    }
  }
</style>
